import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { MEDIA_TYPE_VIDEO } from '../../util/listingMedia';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';

import { IconArrowHead, ResponsiveImage } from '../../components';

import ListingCardVideo from './ListingCardVideo';

import css from './ListingCardMediaCarousel.module.css';
import listingCardCss from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { nearViewportMargin: 600 });

/** Horizontal drag distance required to change slides. */
export const SWIPE_THRESHOLD_PX = 40;

/**
 * In-card media carousel: swipe, prev/next, and pagination dots.
 * Callers should only mount this when media.length >= 2.
 *
 * Navigation uses a real listing <a> so ctrl/cmd/middle-click still open a new tab.
 * Swipes suppress the following click (including mobile ghost clicks after touchend).
 * Active video slides use ListingCardVideo (dwell-gated autoplay).
 */
const ListingCardMediaCarousel = props => {
  const {
    media,
    title,
    renderSizes,
    variantPrefix,
    lazyLoadImage,
    intl,
    listingHref,
    listingLinkLabel,
  } = props;

  const [index, setIndex] = useState(0);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    swiped: false,
  });
  const navRef = useRef({});
  // Suppress the click that follows a swipe (and touch→mouse ghost clicks after touchend).
  const suppressClickRef = useRef(false);
  const ignoreMouseUntilRef = useRef(0);

  const slideCount = media.length;
  const safeIndex = Math.min(index, Math.max(slideCount - 1, 0));
  const active = media[safeIndex];
  const canGoPrev = safeIndex > 0;
  const canGoNext = safeIndex < slideCount - 1;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  navRef.current = {
    canGoPrev,
    canGoNext,
    safeIndex,
  };

  const goTo = nextIndex => {
    setIndex(Math.max(0, Math.min(slideCount - 1, nextIndex)));
  };

  const stopNav = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePrev = event => {
    stopNav(event);
    if (canGoPrev) {
      goTo(safeIndex - 1);
    }
  };

  const handleNext = event => {
    stopNav(event);
    if (canGoNext) {
      goTo(safeIndex + 1);
    }
  };

  const resetDrag = () => {
    dragRef.current = {
      active: false,
      startX: 0,
      startY: 0,
      swiped: false,
    };
  };

  const beginDrag = (clientX, clientY, event) => {
    if (event?.button != null && event.button !== 0) {
      return;
    }
    // Arrows handle their own clicks; don't start a swipe from them.
    if (event?.target?.closest?.('button')) {
      return;
    }
    dragRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      swiped: false,
    };
  };

  const moveDrag = (clientX, clientY) => {
    const drag = dragRef.current;
    if (!drag.active) {
      return;
    }
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX && Math.abs(dy) < SWIPE_THRESHOLD_PX) {
      return;
    }
    // Prefer vertical scroll when the gesture is mostly vertical.
    if (Math.abs(dy) > Math.abs(dx)) {
      drag.active = false;
      return;
    }
    drag.swiped = true;
  };

  const endDrag = (clientX, clientY) => {
    const drag = dragRef.current;
    if (!drag.active) {
      return;
    }
    const { canGoNext: nextOk, canGoPrev: prevOk, safeIndex: idx } = navRef.current;
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    const wasSwipe = drag.swiped || Math.abs(dx) >= SWIPE_THRESHOLD_PX;

    if (wasSwipe && Math.abs(dx) >= Math.abs(dy) && Math.abs(dx) >= SWIPE_THRESHOLD_PX) {
      suppressClickRef.current = true;
      if (dx < 0 && nextOk) {
        setIndex(idx + 1);
      } else if (dx > 0 && prevOk) {
        setIndex(idx - 1);
      }
    }

    // Navigation is handled by the listing <a> click, not here.
    resetDrag();
  };

  // Finish mouse drags even if the pointer is released outside the card.
  useEffect(() => {
    const onMouseMove = event => moveDrag(event.clientX, event.clientY);
    const onMouseUp = event => endDrag(event.clientX, event.clientY);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleMouseDown = event => {
    // Ignore synthetic mouse events that browsers fire after a touch gesture.
    if (Date.now() < ignoreMouseUntilRef.current) {
      return;
    }
    beginDrag(event.clientX, event.clientY, event);
  };

  const touchPoint = event => event.changedTouches?.[0] || event.touches?.[0];
  const handleTouchStart = event => {
    const point = touchPoint(event);
    if (point) {
      beginDrag(point.clientX, point.clientY, event);
    }
  };
  const handleTouchMove = event => {
    const point = touchPoint(event);
    if (point) {
      moveDrag(point.clientX, point.clientY);
    }
  };
  const handleTouchEnd = event => {
    const point = touchPoint(event);
    if (point) {
      endDrag(point.clientX, point.clientY);
    } else {
      resetDrag();
    }
    // Block follow-up mouse compatibility events from starting a second drag/click cycle.
    ignoreMouseUntilRef.current = Date.now() + 500;
  };

  const handleListingLinkClick = event => {
    if (suppressClickRef.current) {
      event.preventDefault();
      suppressClickRef.current = false;
    }
  };

  const renderActiveSlide = () => {
    if (active?.type === MEDIA_TYPE_VIDEO) {
      return <ListingCardVideo video={active.video} title={title} />;
    }

    const image = active?.image || null;
    const variants = image
      ? Object.keys(image?.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
      : [];

    return (
      <ImageComponent
        rootClassName={listingCardCss.rootForImage}
        alt={title}
        image={image}
        variants={variants}
        sizes={renderSizes}
      />
    );
  };

  return (
    <div
      className={css.root}
      data-testid="listing-card-media-carousel"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={resetDrag}
    >
      <Link
        className={css.listingLink}
        to={listingHref}
        onClick={handleListingLinkClick}
        tabIndex={-1}
        aria-label={listingLinkLabel}
        draggable={false}
      >
        <div className={css.slide}>{renderActiveSlide()}</div>
      </Link>

      <button
        type="button"
        className={classNames(css.arrow, css.arrowPrev)}
        onClick={handlePrev}
        disabled={!canGoPrev}
        aria-label={intl.formatMessage({ id: 'ListingCard.previousMedia' })}
      >
        <IconArrowHead direction="left" size="small" rootClassName={css.arrowIcon} />
      </button>
      <button
        type="button"
        className={classNames(css.arrow, css.arrowNext)}
        onClick={handleNext}
        disabled={!canGoNext}
        aria-label={intl.formatMessage({ id: 'ListingCard.nextMedia' })}
      >
        <IconArrowHead direction="right" size="small" rootClassName={css.arrowIcon} />
      </button>

      <div className={css.dots} aria-hidden="true">
        {media.map((item, i) => (
          <span
            key={item.id}
            data-testid="listing-card-media-dot"
            data-active={i === safeIndex ? 'true' : 'false'}
            className={classNames(css.dot, { [css.dotActive]: i === safeIndex })}
          />
        ))}
      </div>
    </div>
  );
};

export default ListingCardMediaCarousel;
